/**
 * DEX listing & indexing roadmap — shared, isomorphic config.
 *
 * The card in the purchase section renders official outbound badges plus a
 * per-phase status chip. Statuses are served by /api/public/dex-status so they
 * can move (presale → liquidity → indexing) without a redeploy; if that call
 * fails the UI silently falls back to DEFAULT_PHASE_STATUS.
 */
export type PhaseStatus = "active" | "next" | "planned";
export type PhaseId = "phase1" | "phase2" | "phase3";

export const PHASE_IDS: readonly PhaseId[] = ["phase1", "phase2", "phase3"];

/** Offline fallback used during SSR, before the fetch resolves, and on error. */
export const DEFAULT_PHASE_STATUS: Record<PhaseId, PhaseStatus> = {
  phase1: "active",
  phase2: "next",
  phase3: "planned",
};

export type DexBadge = {
  name: string;
  /** Official destination opened in a new tab. */
  url: string;
};

export const DEX_BADGES: readonly DexBadge[] = [
  { name: "Jupiter", url: "https://jup.ag/" },
  { name: "Raydium", url: "https://raydium.io/" },
  { name: "CoinGecko", url: "https://www.coingecko.com/" },
  { name: "CoinMarketCap", url: "https://coinmarketcap.com/" },
];

const VALID: readonly string[] = ["active", "next", "planned"];

/** Normalises an untrusted payload into a complete, valid status map. */
export function parsePhaseStatus(input: unknown): Record<PhaseId, PhaseStatus> {
  const out = { ...DEFAULT_PHASE_STATUS };
  if (!input || typeof input !== "object") return out;
  const rec = input as Record<string, unknown>;
  for (const id of PHASE_IDS) {
    const v = rec[id];
    if (typeof v === "string" && VALID.includes(v)) out[id] = v as PhaseStatus;
  }
  return out;
}
